import type { Session } from "@openanalyst-ai/sdk/v2/client"
import { Avatar } from "@openanalyst-ai/ui/avatar"
import { Button } from "@openanalyst-ai/ui/button"
import { ContextMenu } from "@openanalyst-ai/ui/context-menu"
import { useDialog } from "@openanalyst-ai/ui/context/dialog"
import { Dialog } from "@openanalyst-ai/ui/dialog"
import { DropdownMenu } from "@openanalyst-ai/ui/dropdown-menu"
import { Icon } from "@openanalyst-ai/ui/icon"
import { IconButton } from "@openanalyst-ai/ui/icon-button"
import { Spinner } from "@openanalyst-ai/ui/spinner"
import { TextField } from "@openanalyst-ai/ui/text-field"
import { Tooltip } from "@openanalyst-ai/ui/tooltip"
import { showToast } from "@openanalyst-ai/ui/toast"
import { base64Encode } from "@openanalyst-ai/core/util/encode"
import { getFilename } from "@openanalyst-ai/core/util/path"
import { A, useNavigate, useParams } from "@solidjs/router"
import { type Accessor, createMemo, createSignal, For, type JSX, Match, onCleanup, Show, Switch } from "solid-js"
import { createStore } from "solid-js/store"
import openanalyst from "@/assets/openanalyst-project-icon.png"
import { useGlobalSync } from "@/context/global-sync"
import { useLanguage } from "@/context/language"
import { getAvatarColors, type LocalProject, useLayout } from "@/context/layout"
import { useNotification } from "@/context/notification"
import { usePermission } from "@/context/permission"
import { PluginAppIcon } from "@/plugins/plugin-app-icon"
import { messageAgentColor } from "@/utils/agent"
import { sessionTitle } from "@/utils/session-title"
import { childSessionOnPath, compactSessionTime } from "./helpers"
import { threadHasFeedbackRequest, threadIsWorking } from "./status"
import { sidebarClipboardCopyFailedMessage, sidebarClipboardCopyFailedTitle } from "./sidebar-copy"

const OPENANALYST_PROJECT_ID = "4b0ea68d7af9a6031a7ffda7ad66e0cb83315750"

export function getProjectAvatarSource(id?: string, icon?: { color?: string; url?: string; override?: string }) {
  if (id === OPENANALYST_PROJECT_ID) return openanalyst
  if (icon?.override) return icon?.override
  if (icon?.color) return undefined
  return icon?.url
}

export type ProjectStatusKind = "permission" | "response" | "running"

export const ProjectIcon = (props: {
  project: LocalProject
  class?: string
  notify?: boolean
  notifyKind?: ProjectStatusKind
  /** This is the active/selected project (steady green dot when nothing else). */
  active?: boolean
  badgeKind?: "smartwork" | "workspace"
  badgeLabel?: string
}): JSX.Element => {
  const name = createMemo(() => props.project.name || getFilename(props.project.worktree))
  const badgeLabel = (kind: "smartwork" | "workspace") =>
    props.badgeLabel ?? (kind === "smartwork" ? "Smartwork Cloud access enabled" : "OpenAnalyst workspaces enabled")

  // Bottom-right STATUS DOT, most-urgent first:
  //   running   → orange, blinking (AI working)
  //   question  → yellow, blinking (a tool is asking the user — "permission")
  //   finished  → blue, steady (turn complete, chat not yet opened — "response")
  //   active    → green, steady (the currently selected project, otherwise idle)
  const dot = createMemo<{ color: string; blink: boolean; label: string } | undefined>(() => {
    if (props.notifyKind === "running") return { color: "#f97316", blink: true, label: "Running" }
    if (props.notifyKind === "permission") return { color: "#eab308", blink: true, label: "Waiting for your answer" }
    if (props.notifyKind === "response") return { color: "#3b82f6", blink: false, label: "New response" }
    if (props.active) return { color: "#22c55e", blink: false, label: "Active project" }
    return undefined
  })

  return (
    <div class={`relative size-8 shrink-0 rounded ${props.class ?? ""}`}>
      <div class="size-full rounded overflow-clip">
        <Avatar
          fallback={name()}
          src={getProjectAvatarSource(props.project.id, props.project.icon)}
          {...getAvatarColors(props.project.icon?.color)}
          class="size-full rounded"
        />
      </div>
      <Show when={dot()}>
        {(d) => (
          <div
            role="img"
            aria-label={d().label}
            title={d().label}
            classList={{
              "absolute -bottom-0.5 -right-0.5 size-2 rounded-full z-10 ring-2 ring-background-base": true,
              "animate-pulse": d().blink,
            }}
            style={{ "background-color": d().color }}
          />
        )}
      </Show>
      <Show when={props.badgeKind}>
        {(kind) => (
          <span
            data-sidebar-workspace-badge=""
            data-sidebar-smartwork-badge={kind() === "smartwork" ? "" : undefined}
            role="img"
            aria-label={badgeLabel(kind())}
            title={badgeLabel(kind())}
          >
            <Show when={kind() === "smartwork"} fallback={<img src={openanalyst} alt="" aria-hidden="true" />}>
              <Icon name="cloud-upload" size="small" />
            </Show>
          </span>
        )}
      </Show>
    </div>
  )
}

export type SessionItemProps = {
  session: Session
  list: Session[]
  navList?: Accessor<Session[]>
  slug: string
  mobile?: boolean
  dense?: boolean
  showTooltip?: boolean
  showChild?: boolean
  level?: number
  sidebarExpanded: Accessor<boolean>
  clearHoverProjectSoon: () => void
  prefetchSession: (session: Session, priority?: "high" | "low") => void
  archiveSession: (session: Session) => Promise<void>
  viewTitle?: (session: Session) => string
  viewAlias?: (session: Session) => string
  projectLabel?: (session: Session) => string | undefined
  pluginOrigin?: (session: Session) => SessionPluginOrigin | undefined
  context?: (session: Session) => SessionItemContext
  isPinned?: (session: Session) => boolean
  togglePinned?: (session: Session) => void
  hasLocalViewTitle?: (session: Session) => boolean
  promoteViewTitle?: (session: Session) => void | Promise<void>
  renameViewTitle?: (session: Session, title: string) => void
  resetViewTitle?: (session: Session) => void
  renameSessionTitle?: (session: Session, title: string) => void | Promise<void>
  openDirectory?: (session: Session) => void | Promise<void>
  canOpenDirectory?: (session: Session) => boolean
  forkSession?: (session: Session) => void | Promise<void>
  now?: Accessor<number>
}

export type SessionItemContext = {
  project?: string
  workspace?: string
  stage?: string
  pinned?: boolean
}

export type SessionPluginOrigin = {
  id: string
  title: string
  icon: string
  capabilities?: string[]
  href: string
}

const SessionRow = (props: {
  session: Session
  slug: string
  title: Accessor<string>
  subtitle: Accessor<string | undefined>
  pluginOrigin: Accessor<SessionPluginOrigin | undefined>
  mobile?: boolean
  dense?: boolean
  tint: Accessor<string | undefined>
  isWorking: Accessor<boolean>
  hasFeedback: Accessor<boolean>
  hasError: Accessor<boolean>
  unseenCount: Accessor<number>
  timeLabel: Accessor<string>
  timeTitle: Accessor<string>
  clearHoverProjectSoon: () => void
  sidebarOpened: Accessor<boolean>
  warmPress: () => void
  warmFocus: () => void
  openPluginOrigin: (href: string) => void
}): JSX.Element => {
  return (
    <div
      data-component="sidebar-session-row"
      class="flex items-center gap-2 min-w-0 w-full text-left focus:outline-none"
    >
      <A
        href={`/${props.slug}/session/${props.session.id}`}
        class="flex items-center gap-2 min-w-0 flex-1 text-left focus:outline-none"
        classList={{
          "h-8 rounded-md px-2": !props.dense,
          "h-7 rounded-md px-2": !!props.dense,
        }}
        onPointerDown={props.warmPress}
        onFocus={props.warmFocus}
        onClick={() => {
          if (props.sidebarOpened()) return
          props.clearHoverProjectSoon()
        }}
      >
        <Show when={props.hasFeedback() || props.hasError() || props.unseenCount() > 0}>
          <div
            class="shrink-0 size-6 flex items-center justify-center"
            style={{ color: props.tint() ?? "var(--icon-interactive-base)" }}
          >
            <Switch>
              <Match when={props.hasFeedback()}>
                <div class="size-1.5 rounded-full bg-surface-warning-strong" />
              </Match>
              <Match when={props.hasError()}>
                <div class="size-1.5 rounded-full bg-text-diff-delete-base" />
              </Match>
              <Match when={props.unseenCount() > 0}>
                <div class="size-1.5 rounded-full bg-text-interactive-base" />
              </Match>
            </Switch>
          </div>
        </Show>
        <span class="min-w-0 flex-1 flex flex-col">
          <span class="text-14-regular text-text-strong min-w-0 truncate">{props.title()}</span>
          <Show when={props.subtitle()}>
            {(subtitle) => <span class="text-12-regular text-text-weak min-w-0 truncate">{subtitle()}</span>}
          </Show>
        </span>
        <span
          class="shrink-0 min-w-6 text-right text-12-regular text-text-weak tabular-nums flex items-center justify-end"
          title={props.timeTitle()}
          style={{ color: props.isWorking() ? (props.tint() ?? "var(--icon-interactive-base)") : undefined }}
        >
          <Show when={props.isWorking()} fallback={props.timeLabel()}>
            <Spinner class="size-[13px]" />
          </Show>
        </span>
      </A>
      <Show when={props.pluginOrigin()}>
        {(origin) => (
          <Tooltip value={`Open ${origin().title}`} placement={props.mobile ? "bottom" : "right"}>
            <button
              type="button"
              data-session-plugin-origin={origin().id}
              aria-label={`Open ${origin().title}`}
              title={`Open ${origin().title}`}
              class="flex size-6 shrink-0 items-center justify-center rounded-md text-icon-base transition-colors hover:bg-surface-base-hover hover:text-icon-strong focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base"
              onPointerDown={(event) => {
                event.stopPropagation()
                props.warmPress()
              }}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                props.openPluginOrigin(origin().href)
              }}
            >
              <PluginAppIcon
                id={origin().id}
                icon={origin().icon}
                title={origin().title}
                capabilities={origin().capabilities}
                size="small"
              />
            </button>
          </Tooltip>
        )}
      </Show>
    </div>
  )
}

export const SessionItem = (props: SessionItemProps): JSX.Element => {
  const params = useParams()
  const navigate = useNavigate()
  const layout = useLayout()
  const language = useLanguage()
  const dialog = useDialog()
  const notification = useNotification()
  const permission = usePermission()
  const globalSync = useGlobalSync()
  const unseenCount = createMemo(() => notification.session.unseenCount(props.session.id))
  const hasError = createMemo(() => notification.session.unseenHasError(props.session.id))
  const [sessionStore] = globalSync.child(props.session.directory)
  const canonicalTitle = createMemo(() => sessionTitle(props.session.title) ?? "Untitled")
  const title = createMemo(() => props.viewTitle?.(props.session) ?? canonicalTitle())
  const subtitle = createMemo(() => props.projectLabel?.(props.session))
  const pluginOrigin = createMemo(() => props.pluginOrigin?.(props.session))
  const sessionTime = createMemo(() => props.session.time.updated ?? props.session.time.created)
  const timeLabel = createMemo(() => compactSessionTime(sessionTime(), props.now?.() ?? Date.now()))
  const timeTitle = createMemo(() => new Date(sessionTime()).toLocaleString())
  const context = createMemo(() => props.context?.(props.session) ?? {})
  const hasFeedback = createMemo(() => {
    return threadHasFeedbackRequest(sessionStore, props.session.id, (item) => {
      return !permission.autoResponds(item, props.session.directory)
    })
  })
  const isWorking = createMemo(() => {
    if (hasFeedback()) return false
    return threadIsWorking(sessionStore, props.session.id)
  })

  const tint = createMemo(() => messageAgentColor(sessionStore.message[props.session.id], sessionStore.agent))
  const tooltip = createMemo(() => props.showTooltip ?? (props.mobile || !props.sidebarExpanded()))
  const currentChild = createMemo(() => {
    if (!props.showChild) return
    return childSessionOnPath(sessionStore.session, props.session.id, params.id)
  })

  const warm = (span: number, priority: "high" | "low") => {
    const nav = props.navList?.()
    const list = nav?.some((item) => item.id === props.session.id && item.directory === props.session.directory)
      ? nav
      : props.list

    props.prefetchSession(props.session, priority)

    const idx = list.findIndex((item) => item.id === props.session.id && item.directory === props.session.directory)
    if (idx === -1) return

    for (let step = 1; step <= span; step++) {
      const next = list[idx + step]
      if (next) props.prefetchSession(next, step === 1 ? "high" : priority)

      const prev = list[idx - step]
      if (prev) props.prefetchSession(prev, step === 1 ? "high" : priority)
    }
  }

  const item = (
    <SessionRow
      session={props.session}
      slug={props.slug}
      title={title}
      subtitle={subtitle}
      pluginOrigin={pluginOrigin}
      mobile={props.mobile}
      dense={props.dense}
      tint={tint}
      isWorking={isWorking}
      hasFeedback={hasFeedback}
      hasError={hasError}
      unseenCount={unseenCount}
      timeLabel={timeLabel}
      timeTitle={timeTitle}
      clearHoverProjectSoon={props.clearHoverProjectSoon}
      sidebarOpened={layout.sidebar.opened}
      warmPress={() => warm(2, "high")}
      warmFocus={() => warm(2, "high")}
      openPluginOrigin={(href) => {
        if (!layout.sidebar.opened()) props.clearHoverProjectSoon()
        navigate(href)
      }}
    />
  )
  const pinLabel = createMemo(() =>
    props.isPinned?.(props.session) ? language.t("sidebar.thread.unpin") : language.t("sidebar.thread.pin"),
  )
  const canPin = createMemo(() => !props.level && !!props.togglePinned)
  const hasPromotableTitle = createMemo(() => !!props.hasLocalViewTitle?.(props.session))
  const canOpenDirectory = createMemo(() => !!props.canOpenDirectory?.(props.session))
  const [confirmingDelete, setConfirmingDelete] = createSignal(false)
  const showRowActions = createMemo(() => !props.level || canPin())
  const deleteLabel = () => language.t("sidebar.thread.archiveChat")
  const startDeleteConfirmation = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setConfirmingDelete(true)
  }
  const confirmDelete = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setConfirmingDelete(false)
    void props.archiveSession(props.session)
  }
  const copyText = (value: string, description = value) =>
    navigator.clipboard
      .writeText(value)
      .then(() => {
        showToast({
          variant: "success",
          icon: "circle-check",
          title: language.t("session.share.copy.copied"),
          description,
        })
      })
      .catch((err) => {
        console.error("[layout:sidebar] session metadata copy failed", err)
        showToast({
          variant: "error",
          title: sidebarClipboardCopyFailedTitle("Chat detail"),
          description: sidebarClipboardCopyFailedMessage(),
        })
      })
  const deeplink = () => `${window.location.origin}/${props.slug}/session/${props.session.id}`

  const openViewLabelDialog = () => {
    dialog.show(() => (
      <DialogThreadLabel
        mode="view"
        title={language.t("sidebar.thread.viewLabelDialog.title")}
        description={language.t("sidebar.thread.viewLabelDialog.description")}
        value={props.viewAlias?.(props.session) ?? canonicalTitle()}
        canonicalTitle={canonicalTitle()}
        project={context().project}
        workspace={context().workspace}
        stage={context().stage}
        pinned={context().pinned}
        onSave={(next) => props.renameViewTitle?.(props.session, next)}
        onClear={() => props.resetViewTitle?.(props.session)}
      />
    ))
  }
  const openRenameDialog = () => {
    dialog.show(() => (
      <DialogThreadLabel
        mode="canonical"
        title={language.t("sidebar.thread.renameChatDialog.title")}
        description={language.t("sidebar.thread.renameChatDialog.description")}
        value={canonicalTitle()}
        canonicalTitle={canonicalTitle()}
        project={context().project}
        workspace={context().workspace}
        stage={context().stage}
        pinned={context().pinned}
        onSave={(next) => props.renameSessionTitle?.(props.session, next)}
      />
    ))
  }

  const DropdownActions = () => (
    <>
      <DropdownMenu.Group>
        <DropdownMenu.GroupLabel>{language.t("sidebar.thread.menu.open")}</DropdownMenu.GroupLabel>
        <DropdownMenu.Item onSelect={() => navigate(`/${props.slug}/session/${props.session.id}`)}>
          <DropdownMenu.ItemLabel>{language.t("sidebar.thread.openChat")}</DropdownMenu.ItemLabel>
        </DropdownMenu.Item>
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      <DropdownMenu.Group>
        <DropdownMenu.GroupLabel>{language.t("sidebar.thread.menu.organize")}</DropdownMenu.GroupLabel>
        <Show when={props.togglePinned}>
          <DropdownMenu.Item data-action="thread-pin" onSelect={() => props.togglePinned?.(props.session)}>
            <DropdownMenu.ItemLabel>{pinLabel()}</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        </Show>
        <Show when={props.renameViewTitle}>
          <DropdownMenu.Item data-action="thread-view-label" onSelect={openViewLabelDialog}>
            <DropdownMenu.ItemLabel>{language.t("sidebar.thread.viewLabel")}</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-action="thread-clear-view-label"
            disabled={!hasPromotableTitle()}
            onSelect={() => props.resetViewTitle?.(props.session)}
          >
            <DropdownMenu.ItemLabel>{language.t("sidebar.thread.clearViewLabel")}</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        </Show>
        <Show when={props.promoteViewTitle}>
          <DropdownMenu.Item
            data-action="thread-promote-view-label"
            disabled={!hasPromotableTitle()}
            onSelect={() => void props.promoteViewTitle?.(props.session)}
          >
            <DropdownMenu.ItemLabel>{language.t("sidebar.thread.promoteViewLabel")}</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        </Show>
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      <DropdownMenu.Group>
        <DropdownMenu.GroupLabel>{language.t("sidebar.thread.menu.chat")}</DropdownMenu.GroupLabel>
        <Show when={props.renameSessionTitle}>
          <DropdownMenu.Item data-action="thread-rename-chat" onSelect={openRenameDialog}>
            <DropdownMenu.ItemLabel>{language.t("sidebar.thread.renameChat")}</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        </Show>
        <DropdownMenu.Item data-action="thread-archive" onSelect={() => void props.archiveSession(props.session)}>
          <DropdownMenu.ItemLabel>{language.t("sidebar.thread.archiveChat")}</DropdownMenu.ItemLabel>
        </DropdownMenu.Item>
        <Show when={props.forkSession}>
          <DropdownMenu.Item data-action="thread-fork" onSelect={() => void props.forkSession?.(props.session)}>
            <DropdownMenu.ItemLabel>{language.t("command.session.fork")}</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        </Show>
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      <DropdownMenu.Group>
        <DropdownMenu.GroupLabel>{language.t("sidebar.thread.menu.context")}</DropdownMenu.GroupLabel>
        <Show when={canOpenDirectory()}>
          <DropdownMenu.Item
            data-action="thread-open-directory"
            class="group/folder-action"
            onSelect={() => void props.openDirectory?.(props.session)}
          >
            <DropdownMenu.ItemLabel>
              <span class="flex min-w-0 items-center gap-2">
                <Icon
                  name="folder"
                  size="small"
                  class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
                />
                <span class="truncate">{language.t("sidebar.thread.openDirectory")}</span>
              </span>
            </DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        </Show>
        <DropdownMenu.Item
          data-action="thread-copy-directory"
          class="group/folder-action"
          onSelect={() => void copyText(props.session.directory)}
        >
          <DropdownMenu.ItemLabel>
            <span class="flex min-w-0 items-center gap-2">
              <Icon
                name="copy"
                size="small"
                class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
              />
              <span class="truncate">{language.t("sidebar.thread.copyDirectory")}</span>
            </span>
          </DropdownMenu.ItemLabel>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          data-action="thread-copy-session-id"
          onSelect={() => void copyText(props.session.id)}
        >
          <DropdownMenu.ItemLabel>{language.t("sidebar.thread.copySessionID")}</DropdownMenu.ItemLabel>
        </DropdownMenu.Item>
        <DropdownMenu.Item data-action="thread-copy-deeplink" onSelect={() => void copyText(deeplink())}>
          <DropdownMenu.ItemLabel>{language.t("sidebar.thread.copyDeeplink")}</DropdownMenu.ItemLabel>
        </DropdownMenu.Item>
      </DropdownMenu.Group>
    </>
  )
  const ContextActions = () => (
    <>
      <ContextMenu.Group>
        <ContextMenu.GroupLabel>{language.t("sidebar.thread.menu.open")}</ContextMenu.GroupLabel>
        <ContextMenu.Item onSelect={() => navigate(`/${props.slug}/session/${props.session.id}`)}>
          <ContextMenu.ItemLabel>{language.t("sidebar.thread.openChat")}</ContextMenu.ItemLabel>
        </ContextMenu.Item>
      </ContextMenu.Group>
      <ContextMenu.Separator />
      <ContextMenu.Group>
        <ContextMenu.GroupLabel>{language.t("sidebar.thread.menu.organize")}</ContextMenu.GroupLabel>
        <Show when={props.togglePinned}>
          <ContextMenu.Item data-action="thread-pin" onSelect={() => props.togglePinned?.(props.session)}>
            <ContextMenu.ItemLabel>{pinLabel()}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
        </Show>
        <Show when={props.renameViewTitle}>
          <ContextMenu.Item data-action="thread-view-label" onSelect={openViewLabelDialog}>
            <ContextMenu.ItemLabel>{language.t("sidebar.thread.viewLabel")}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
          <ContextMenu.Item
            data-action="thread-clear-view-label"
            disabled={!hasPromotableTitle()}
            onSelect={() => props.resetViewTitle?.(props.session)}
          >
            <ContextMenu.ItemLabel>{language.t("sidebar.thread.clearViewLabel")}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
        </Show>
        <Show when={props.promoteViewTitle}>
          <ContextMenu.Item
            data-action="thread-promote-view-label"
            disabled={!hasPromotableTitle()}
            onSelect={() => void props.promoteViewTitle?.(props.session)}
          >
            <ContextMenu.ItemLabel>{language.t("sidebar.thread.promoteViewLabel")}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
        </Show>
      </ContextMenu.Group>
      <ContextMenu.Separator />
      <ContextMenu.Group>
        <ContextMenu.GroupLabel>{language.t("sidebar.thread.menu.chat")}</ContextMenu.GroupLabel>
        <Show when={props.renameSessionTitle}>
          <ContextMenu.Item data-action="thread-rename-chat" onSelect={openRenameDialog}>
            <ContextMenu.ItemLabel>{language.t("sidebar.thread.renameChat")}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
        </Show>
        <ContextMenu.Item data-action="thread-archive" onSelect={() => void props.archiveSession(props.session)}>
          <ContextMenu.ItemLabel>{language.t("sidebar.thread.archiveChat")}</ContextMenu.ItemLabel>
        </ContextMenu.Item>
        <Show when={props.forkSession}>
          <ContextMenu.Item data-action="thread-fork" onSelect={() => void props.forkSession?.(props.session)}>
            <ContextMenu.ItemLabel>{language.t("command.session.fork")}</ContextMenu.ItemLabel>
          </ContextMenu.Item>
        </Show>
      </ContextMenu.Group>
      <ContextMenu.Separator />
      <ContextMenu.Group>
        <ContextMenu.GroupLabel>{language.t("sidebar.thread.menu.context")}</ContextMenu.GroupLabel>
        <Show when={canOpenDirectory()}>
          <ContextMenu.Item
            data-action="thread-open-directory"
            class="group/folder-action"
            onSelect={() => void props.openDirectory?.(props.session)}
          >
            <ContextMenu.ItemLabel>
              <span class="flex min-w-0 items-center gap-2">
                <Icon
                  name="folder"
                  size="small"
                  class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
                />
                <span class="truncate">{language.t("sidebar.thread.openDirectory")}</span>
              </span>
            </ContextMenu.ItemLabel>
          </ContextMenu.Item>
        </Show>
        <ContextMenu.Item
          data-action="thread-copy-directory"
          class="group/folder-action"
          onSelect={() => void copyText(props.session.directory)}
        >
          <ContextMenu.ItemLabel>
            <span class="flex min-w-0 items-center gap-2">
              <Icon
                name="copy"
                size="small"
                class="shrink-0 text-icon-weak transition-transform duration-150 ease-out group-hover/folder-action:translate-x-0.5 motion-reduce:transition-none"
              />
              <span class="truncate">{language.t("sidebar.thread.copyDirectory")}</span>
            </span>
          </ContextMenu.ItemLabel>
        </ContextMenu.Item>
        <ContextMenu.Item data-action="thread-copy-session-id" onSelect={() => void copyText(props.session.id)}>
          <ContextMenu.ItemLabel>{language.t("sidebar.thread.copySessionID")}</ContextMenu.ItemLabel>
        </ContextMenu.Item>
        <ContextMenu.Item data-action="thread-copy-deeplink" onSelect={() => void copyText(deeplink())}>
          <ContextMenu.ItemLabel>{language.t("sidebar.thread.copyDeeplink")}</ContextMenu.ItemLabel>
        </ContextMenu.Item>
      </ContextMenu.Group>
    </>
  )

  return (
    <>
      <ContextMenu
        modal={!layout.sidebar.opened()}
        onOpenChange={(open) => {
          if (!open) return
          warm(2, "high")
        }}
      >
        <ContextMenu.Trigger
          as="div"
          data-session-id={props.session.id}
          class="group/session relative w-full min-w-0 rounded-md cursor-default transition-colors hover:bg-surface-raised-base-hover [&:has(:focus-visible)]:bg-surface-raised-base-hover has-[[data-expanded]]:bg-surface-raised-base-hover has-[.active]:bg-surface-base-active"
          classList={{
            "pl-2": !props.level,
            "pr-8": showRowActions(),
          }}
          style={{ "padding-left": props.level ? `${8 + props.level * 16}px` : undefined }}
        >
          <div class="flex min-w-0 items-center gap-1">
            <div class="min-w-0 flex-1">
              <Show
                when={!tooltip()}
                fallback={
                  <Tooltip
                    placement={props.mobile ? "bottom" : "right"}
                    value={
                      canonicalTitle() === title()
                        ? canonicalTitle()
                        : `${title()} · ${language.t("sidebar.thread.canonicalTitle")}: ${canonicalTitle()}`
                    }
                    gutter={10}
                    class="min-w-0 w-full"
                  >
                    {item}
                  </Tooltip>
                }
              >
                {item}
              </Show>
            </div>

            <Show when={showRowActions()}>
              <div
                class="absolute right-1 top-1/2 z-10 -translate-y-1/2 flex items-center gap-0.5 rounded-md bg-surface-raised-base-hover p-0.5 transition-opacity"
                classList={{
                  "opacity-100 pointer-events-auto": !!props.mobile || confirmingDelete(),
                  "opacity-0 pointer-events-none": !props.mobile && !confirmingDelete(),
                  "group-hover/session:opacity-100 group-hover/session:pointer-events-auto": true,
                  "group-focus-within/session:opacity-100 group-focus-within/session:pointer-events-auto": true,
                  "has-[[data-expanded]]:opacity-100 has-[[data-expanded]]:pointer-events-auto":
                    true,
                }}
              >
                <Show when={canPin()}>
                  <Tooltip value={pinLabel()} placement="top">
                    <IconButton
                      icon={props.isPinned?.(props.session) ? "unpin" : "pin"}
                      variant="ghost"
                      class="size-6 rounded-md data-[expanded]:bg-surface-base-active"
                      aria-label={pinLabel()}
                      data-action="thread-hover-pin"
                      onClick={(event: MouseEvent) => {
                        event.preventDefault()
                        event.stopPropagation()
                        props.togglePinned?.(props.session)
                      }}
                    />
                  </Tooltip>
                </Show>
                <Show
                  when={confirmingDelete()}
                  fallback={
                    <Tooltip value={deleteLabel()} placement="top">
                      <IconButton
                        icon="trash"
                        variant="ghost"
                        class="size-6 rounded-md text-icon-base hover:text-text-diff-delete-base"
                        aria-label={deleteLabel()}
                        data-action="thread-delete"
                        onClick={startDeleteConfirmation}
                      />
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    data-action="thread-delete-confirm"
                    class="h-6 rounded-full border border-[color-mix(in_srgb,var(--text-diff-delete-base)_22%,var(--border-weaker-base))] bg-[color-mix(in_srgb,var(--text-diff-delete-base)_10%,var(--background-base))] px-2.5 text-12-medium text-text-diff-delete-base transition-colors hover:bg-[color-mix(in_srgb,var(--text-diff-delete-base)_16%,var(--background-base))] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong-base"
                    onClick={confirmDelete}
                    onMouseLeave={() => setConfirmingDelete(false)}
                    onBlur={() => setConfirmingDelete(false)}
                  >
                    Confirm
                  </button>
                </Show>
                <DropdownMenu modal={!layout.sidebar.opened()}>
                  <Tooltip value={language.t("common.moreOptions")} placement="top">
                    <DropdownMenu.Trigger
                      as={IconButton}
                      icon="dot-grid"
                      variant="ghost"
                      class="size-6 rounded-md data-[expanded]:bg-surface-base-active"
                      aria-label={language.t("common.moreOptions")}
                      data-action="thread-actions"
                      onClick={(event: MouseEvent) => {
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                    />
                  </Tooltip>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content class="mt-1 min-w-56">
                      <DropdownActions />
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu>
              </div>
            </Show>
          </div>
        </ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content class="min-w-56">
            <ContextActions />
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu>
      <Show when={currentChild()} keyed>
        {(child) => (
          <div class="w-full">
            <SessionItem {...props} session={child} level={(props.level ?? 0) + 1} />
          </div>
        )}
      </Show>
    </>
  )
}

const DialogThreadLabel = (props: {
  mode: "canonical" | "view"
  title: string
  description: string
  value: string
  canonicalTitle: string
  project?: string
  workspace?: string
  stage?: string
  pinned?: boolean
  onSave: (value: string) => void | Promise<void>
  onClear?: () => void
}) => {
  const dialog = useDialog()
  const language = useLanguage()
  const [store, setStore] = createStore({ value: props.value })
  const trimmed = () => store.value.trim()
  const save = (event: SubmitEvent) => {
    event.preventDefault()
    if (!trimmed()) return
    void props.onSave(trimmed())
    dialog.close()
  }

  return (
    <Dialog title={props.title} description={props.description} class="w-full max-w-[420px] mx-auto">
      <form onSubmit={save} class="flex flex-col gap-5 p-6 pt-0">
        <div class="grid gap-2 text-12-regular text-text-weak">
          <div>
            <span class="text-text-base">{language.t("sidebar.thread.canonicalTitle")}:</span> {props.canonicalTitle}
          </div>
          <Show when={props.project}>
            {(project) => (
              <div>
                <span class="text-text-base">{language.t("sidebar.thread.project")}:</span> {project()}
              </div>
            )}
          </Show>
          <Show when={props.workspace}>
            {(workspace) => (
              <div>
                <span class="text-text-base">{language.t("sidebar.thread.workspace")}:</span> {workspace()}
              </div>
            )}
          </Show>
          <Show when={props.stage}>
            {(stage) => (
              <div>
                <span class="text-text-base">{language.t("sidebar.thread.stage")}:</span> {stage()}
              </div>
            )}
          </Show>
          <div>
            <span class="text-text-base">{language.t("sidebar.thread.pinState")}:</span>{" "}
            {props.pinned ? language.t("sidebar.thread.pinned") : language.t("sidebar.thread.notPinned")}
          </div>
        </div>
        <TextField
          autofocus
          value={store.value}
          label={
            props.mode === "view"
              ? language.t("sidebar.thread.viewLabelInput")
              : language.t("sidebar.thread.renameChatInput")
          }
          onChange={(value) => setStore("value", value)}
        />
        <div class="flex items-center justify-between gap-3">
          <Show when={props.onClear}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                props.onClear?.()
                dialog.close()
              }}
            >
              {language.t("sidebar.thread.clearViewLabel")}
            </Button>
          </Show>
          <div class="ml-auto flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => dialog.close()}>
              {language.t("common.cancel")}
            </Button>
            <Button type="submit" variant="primary" disabled={!trimmed()}>
              {language.t("common.save")}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  )
}

export const NewSessionItem = (props: {
  slug: string
  mobile?: boolean
  dense?: boolean
  sidebarExpanded: Accessor<boolean>
  clearHoverProjectSoon: () => void
}): JSX.Element => {
  const layout = useLayout()
  const language = useLanguage()
  const label = language.t("command.session.new")
  const tooltip = () => props.mobile || !props.sidebarExpanded()
  const item = (
    <A
      href={`/${props.slug}/session`}
      end
      class={`flex items-center gap-2 min-w-0 w-full text-left focus:outline-none ${props.dense ? "py-0.5" : "py-1"}`}
      onClick={() => {
        if (layout.sidebar.opened()) return
        props.clearHoverProjectSoon()
      }}
    >
      <div class="shrink-0 size-6 flex items-center justify-center">
        <Icon name="new-session" size="small" class="text-icon-weak" />
      </div>
      <span class="text-14-regular text-text-strong min-w-0 flex-1 truncate">{label}</span>
    </A>
  )

  return (
    <div class="group/session relative w-full min-w-0 rounded-md cursor-default transition-colors pl-2 pr-3 hover:bg-surface-raised-base-hover [&:has(:focus-visible)]:bg-surface-raised-base-hover has-[.active]:bg-surface-base-active">
      <Show
        when={!tooltip()}
        fallback={
          <Tooltip placement={props.mobile ? "bottom" : "right"} value={label} gutter={10} class="min-w-0 w-full">
            {item}
          </Tooltip>
        }
      >
        {item}
      </Show>
    </div>
  )
}

export const SessionSkeleton = (props: { count?: number }): JSX.Element => {
  const items = Array.from({ length: props.count ?? 4 }, (_, index) => index)
  return (
    <div class="flex flex-col gap-1">
      <For each={items}>
        {() => <div class="h-8 w-full rounded-md bg-surface-raised-base opacity-60 animate-pulse" />}
      </For>
    </div>
  )
}
